import React, { useState } from 'react'
import { Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material'
import { GoogleMap, Polygon, Marker,
  useLoadScript } from '@react-google-maps/api';
import { FieldBoundaryResultType, useGetFieldBoundaryMutation } from '../store/services/mapservice'
import AutoComplete from '../components/googlemap/Autocomplete';

const containerStyle = {
  width: '100wv',
  height: '70vh'
};

const center = {
  lat: 33,
  lng: -90
};

const libraries: any = ['places'];
let marker:any = null

// Point-in-polygon detection function
const isPointInPolygon = (point: { lat: number, lng: number }, polygon: Array<{ lat: number, lng: number }>) => {
  const { lat, lng } = point;
  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  
  return inside;
};

const MyGoogleMap = () => {
  const [triangleCoords, setTriangleCoords] = useState([])
  const [obstacleCoords, setObstacleCoords] = useState<Array<Array<{lat: number, lng: number}>>>([])
  const [zoom] = useState(5)
  const [polygonString, setPolygonString] = useState<string>('')
  const [errorModal, setErrorModal] = useState({ open: false, message: '' })

  // Helper function to convert polygon coordinates to WKT format
  const convertPolygonToString = (coords: Array<{lat: number, lng: number}>) => {
    if (!coords || coords.length === 0) return ''
    // WKT format: POLYGON ((lng lat, lng lat, ..., lng lat))
    // Note: WKT uses lng,lat order (longitude first, latitude second)
    const coordStrings = coords.map(coord => `${coord.lng} ${coord.lat}`).join(', ')
    // Close the polygon by adding the first coordinate at the end
    const closedCoords = coords.length > 0 ? `${coordStrings}, ${coords[0].lng} ${coords[0].lat}` : coordStrings
    return `POLYGON ((${closedCoords}))`
  }

  const { isLoaded } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyCGR6G60H6j2Q2UJHYWYjaAraD8tYzBhsU",
    libraries
  })

  const [getFieldBoundary] = useGetFieldBoundaryMutation<FieldBoundaryResultType>()
  const [map, setMap] = React.useState<google.maps.Map | null>(null)

  const onLoad = React.useCallback(function callback(map) {
    // This is just an example of getting and using the map instance!!! don't just blindly copy!
    //const bounds = new window.google.maps.LatLngBounds(center);
    //map.fitBounds(bounds);

    setMap(map)
  }, [])

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null)
  }, [])

  const handleClick = (props: any) => {
    const clickPoint = { lat: props.latLng.lat(), lng: props.latLng.lng() };
    
    // Check if click is within existing polygon
    const isInsidePolygon = triangleCoords.length > 0 && isPointInPolygon(clickPoint, triangleCoords);
    
    if (isInsidePolygon) {
      // Click is inside polygon, make call with obstacle: "True" and pass polygon string
      getFieldBoundary({ 
        lat: clickPoint.lat, 
        lon: clickPoint.lng, 
        obstacle: true,
        polygon: polygonString 
      }).then((r: any) => {
        // Validate response exists
        if (!r || !r.data) {
          setErrorModal({ open: true, message: 'No Valid Obstacle Boundary' })
          return
        }

        // Validate response has features data
        if (!r.data.features || r.data.features.length === 0) {
          setErrorModal({ open: true, message: 'No Valid Obstacle Boundary' })
          return
        }

        const coordinates = r?.data?.features[0]?.geometry?.coordinates?.map((p: any) => p.map((a: any) => ({"lng": a[0], "lat": a[1]})))
        
        if (!coordinates || coordinates.length === 0) {
          setErrorModal({ open: true, message: 'No Valid Obstacle Boundary' })
          return
        }

        // Add new obstacle polygon to existing array
        setObstacleCoords(prevCoords => [...prevCoords, coordinates[0]])
      }).catch((error: any) => {
        setErrorModal({ open: true, message: 'No Valid Obstacle Boundary' })
      })
    } else {
      // Click is outside polygon, make normal call
      getFieldBoundary({ lat: clickPoint.lat, lon: clickPoint.lng }).then((r: any) => {
        // Validate response exists
        if (!r || !r.data) {
          setErrorModal({ open: true, message: 'No Valid Obstacle Boundary' })
          return
        }

        // Validate response has features data
        if (!r.data.features || r.data.features.length === 0) {
          setErrorModal({ open: true, message: 'No Valid Obstacle Boundary' })
          return
        }

        const coordinates = r?.data?.features[0]?.geometry?.coordinates?.map((p: any) => p.map((a: any) => ({"lng": a[0], "lat": a[1]})))
        
        if (!coordinates || coordinates.length === 0) {
          setErrorModal({ open: true, message: 'No Valid Obstacle Boundary' })
          return
        }

        setTriangleCoords(coordinates[0])
        
        // Convert and store the polygon string for future use
        const newPolygonString = convertPolygonToString(coordinates[0])
        setPolygonString(newPolygonString)
        
        // Clear obstacle coordinates when drawing new field boundary
        setObstacleCoords([])
        
        // Zoom to the new polygon
        if (map && coordinates[0]?.length > 0) {
          const bounds = new google.maps.LatLngBounds();
          coordinates[0].forEach((coord: {lat: number, lng: number}) => {
            bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
          });
          map.fitBounds(bounds);
        }
      }).catch((error: any) => {
        setErrorModal({ open: true, message: 'No Valid Obstacle Boundary' })
      })
    }

    if (marker) {
      marker.setMap(null);
    }

    marker = new google.maps.Marker({
      position: props.latLng,
      // animation: google.maps.Animation.BOUNCE,
      map: map
    });
    marker.setPosition(props.latLng);
  }

  const handleRemoveObstacle = (index: number) => {
    setObstacleCoords(prevCoords => prevCoords.filter((_, i) => i !== index));
  }

  // Calculate center point of a polygon for placing the "x" marker
  const getPolygonCenter = (coords: Array<{lat: number, lng: number}>) => {
    const latSum = coords.reduce((sum, coord) => sum + coord.lat, 0);
    const lngSum = coords.reduce((sum, coord) => sum + coord.lng, 0);
    return {
      lat: latSum / coords.length,
      lng: lngSum / coords.length
    };
  };

  return isLoaded ? (
    <Paper sx={{ height: '90vh', width: '100%' }}>
      { map && <AutoComplete theMap={map} /> }
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        options={{ mapTypeId: 'hybrid', scrollwheel: true,   }}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleClick}
      >
        { triangleCoords?.length 
          && <Polygon paths={triangleCoords} 
            options={{ strokeOpacity: 0.8, 
              strokeColor: "red", 
              fillColor:"#fff", 
              fillOpacity: 0,
              clickable: false
            }} /> 
        }
        { obstacleCoords?.map((coords, index) => {
          const center = getPolygonCenter(coords);
          return (
            <React.Fragment key={index}>
              <Polygon 
                paths={coords} 
                options={{ strokeOpacity: 0.8, 
                  strokeColor: "orange", 
                  fillColor:"#ffa500", 
                  fillOpacity: 0.3,
                  clickable: false
                }} 
              />
              <Marker
                position={center}
                onClick={() => handleRemoveObstacle(index)}
                options={{
                  icon: {
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="white" stroke="red" stroke-width="2"/>
                        <path d="M8 8L16 16M16 8L8 16" stroke="red" stroke-width="2" stroke-linecap="round"/>
                      </svg>
                    `),
                    scaledSize: new google.maps.Size(24, 24),
                    anchor: new google.maps.Point(12, 12)
                  }
                }}
              />
            </React.Fragment>
          );
        })}
      </GoogleMap>

      <Dialog
        open={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Error</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {errorModal.message}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setErrorModal({ open: false, message: '' })}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  ) : <></>
}
export default React.memo(MyGoogleMap)
